import uuidv4 from 'uuid/v4';
import {
    deleteLocalData,
    getCookie,
    getLocalData,
    setCookie,
    setLocalData
} from 'Js/lib/localdata';
import parse from 'Js/api/lib/parse';
import getFirstPartOfPath from 'Js/lib/getFirstPartOfPath';
import globals from 'Js/common/globals';

/**
 * Тип дополнительных данных для передачи через pushEvent или pushData.
 */
export type ProAnalyticsEventCustomProps = Record<
    string,
    string | number | boolean | object
>;

class ProAnalytics {
    /**
     * Время жизни сессии
     * (по умолчанию 30 min).
     */
    private sessionDuration = 1800000;

    /**
     * Название сессионной куки.
     */
    private sessionCookieName = 'user_session';

    /**
     * Ключ сессионного хранилища для стека переходов между страницами.
     */
    private reflogStorageName = 'proAnalyticsReflog';

    /**
     * Максимальное количество сохраняемых в лог записей о переходах.
     * Выбирается эмпирически.
     *
     * Если записей будет слишком мало, это может приводить к потерям связей
     * между pageViewId и referrerPageViewId, то есть к неточностям в аналитике.
     * Если слишком много, то не исключены проблемы с производительностью на клиенте.
     */
    private reflogSize = 20;

    /**
     * Флаг успешной инициализации аналитики.
     * Нельзя отправлять события через неинициализированный экземпляр.
     */
    private hasStarted = false;

    /**
     * Уникальный идентификатор просмотра страницы.
     */
    private pageViewId = ProAnalytics.generatePageViewId();

    /**
     * Уникальный идентификатор просмотра страницы, с которой мы пришли.
     */
    private referrerPageViewId?: string;

    /**
     * Код состояния ответа HTTP.
     */
    private statusCode: number;

    /**
     * Является ли клиент ботом поисковой системы.
     * Значение isSearchSystem доступно только на страницах выдачи.
     */
    private isBot: boolean;

    /**
     * Начальная локация для отслеживания переходов через History API.
     */
    private href: string;

    /**
     * Название раздела, или просто группы страниц, для удобства построения отчётов.
     * Примеры: catalog, makers, autoProducts.
     */
    private pathType: string;

    /**
     * Название раздела, из которого мы пришли.
     * Оно восстанавливается из сессионного хранилища.
     */
    private referrerPathType?: string;

    constructor({
        statusCode,
        isBot,
        href
    }: {
        statusCode: number;
        isBot: boolean;
        href: string;
    }) {
        this.statusCode = statusCode || 200;
        this.isBot = isBot;
        this.href = href;
        this.pathType = ProAnalytics.getPathType(this.href);

        if (this.href) {
            this.savePageView();

            const { urlReferrer } = globals;
            // Если известно, с какого адреса мы попали на текущую страницу,
            // есть смысл поискать в хранилище её pageViewId.
            if (urlReferrer) {
                const referrerPageView = this.restorePageView(urlReferrer);
                this.referrerPathType = referrerPageView?.[1];
                this.referrerPageViewId = referrerPageView?.[2];
            }
        }
    }

    /**
     * Публичное свойство для доступа к идентификаторам pageView,
     * которые бывают нужны в сторонних методах отправки событий.
     */
    public get pageView() {
        return {
            pageViewId: this.pageViewId,
            referrerPageViewId: this.referrerPageViewId
        };
    }

    /**
     * Считывает лог сгенерированных pageViewId из хранилища.
     * Записи в логе имеют вид кортежей [href, pathType, pageViewId].
     */
    private readPageViewReflog(): [
        href: string,
        pathType: string,
        pageViewId: string
    ][] {
        const reflogJson = getLocalData(
            this.reflogStorageName,
            'sessionStorage'
        );
        let reflog;
        if (reflogJson)
            try {
                reflog = JSON.parse(reflogJson);
                if (
                    !(
                        Array.isArray(reflog) &&
                        reflog.every(
                            value =>
                                Array.isArray(value) &&
                                typeof value[0] == 'string' &&
                                typeof value[1] == 'string' &&
                                typeof value[2] == 'string'
                        )
                    )
                ) {
                    throw new TypeError('Invalid pro-analytics reflog format.');
                }
                return reflog;
            } catch {
                deleteLocalData(this.reflogStorageName, 'sessionStorage');
            }
        return [];
    }

    /**
     * Сохраняет сгенерированный pageViewId в лог,
     * для того, чтобы другая страница, ссылающаяся на данную страницу через referrer,
     * могла извлечь pageViewId данной страницы из хранилища
     * и отправить его как referrerPageViewId.
     */
    private savePageView() {
        const newRecord = [this.href, this.pathType, this.pageViewId];
        const reflog = this.readPageViewReflog();

        setLocalData(
            this.reflogStorageName,
            JSON.stringify([newRecord, ...reflog].slice(0, this.reflogSize)),
            'sessionStorage'
        );
    }

    /**
     * Ищет запись в логе pageViewId по соответствующему href.
     * Ищется только первая запись, и это конечно может приводить к ошибочным
     * подстановкам referrerPageViewId, если в логе есть несколько одинаковых href
     * и страницы открывались и загружались хаотично, но точного способа сопоставления
     * нет, будем считать это неизбежным злом, которое не должно часто проявляться.
     * По крайней мере, referrerPathType будет соответствовать.
     */
    private restorePageView(href: string) {
        const reflog = this.readPageViewReflog();
        return reflog.find(([referrerHref]) => referrerHref == href);
    }

    /**
     * Вспомогательный метод для вычисления даты окончания суток.
     */
    static getDayEnd(): Date {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d;
    }

    /**
     * Генерирует уникальный идентификатор просмотра страницы.
     */
    static generatePageViewId(): string {
        return `${uuidv4()}|${
            Date.now() * (Math.floor(Math.random() * 100) + 1)
        }|${uuidv4()}`;
    }

    /**
     * Возвращает название раздела.
     * Название определяется по метаданным страницы ("feed-context", "path-type"),
     * или просто берётся первый сегмент адреса страницы.
     */
    static getPathType(href: string) {
        if (!href) {
            return null;
        }

        const pathType = document
            .querySelector<HTMLMetaElement>('meta[name="path-type"]')
            ?.getAttribute('content');

        // Значение атрибута <meta name="path-type" content="?">
        // Это специальный атрибут для кастомизации pathType отдельных разделов.
        if (pathType) {
            return pathType;
        }

        const { type: feedType, customPathType } =
            document.querySelector<HTMLMetaElement>('meta[name="feed-context"]')
                ?.dataset || {};

        // Значение атрибута <meta name="feed-context" data-custom-path-type="?">
        // Поддерживаем для совместимости, но лучше использовать мету "path-type".
        if (customPathType) {
            return customPathType;
        }
        // Значение атрибута <meta name="feed-context" data-type="?">
        // Поддерживаем для совместимости, но лучше использовать мету "path-type".
        if (feedType) {
            return feedType;
        }

        try {
            return getFirstPartOfPath(new URL(href));
        } catch {
            return null;
        }
    }

    /**
     * Отправляет событие в аналитику.
     * @param {Object} event
     * @returns {Promise}
     */
    static pushAnalyticsData(event): Promise<void> {
        return new Promise((resolve, reject) => {
            const isSuccess = navigator.sendBeacon(
                '/api/v1/analytics/push',
                JSON.stringify(event)
            );
            if (isSuccess) resolve();
            else reject();
        });
    }

    /**
     * Выполняет инициализацию аналитики: создаётся сессия
     * и отправляются события начала сессии и просмотра страницы.
     */
    public start() {
        if (!this.hasStarted && !this.isBot) {
            // Сессия стартует только в активном окне браузера
            // См. https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
            if (document.visibilityState == 'visible') {
                this.hasStarted = true;
                this.initPageView();
                this.initDataLayer();
                this.setEvents();
            }
            // Неактивное окно подписывается на изменение состояния,
            // чтобы начать сессию после первой активации окна
            else if (typeof document.visibilityState !== 'undefined') {
                document.addEventListener('visibilitychange', () => {
                    this.start();
                });
            }
        }
        return this;
    }

    /**
     * Отправляет автоматически отслеживаемое событие просмотра страницы.
     */
    private initPageView() {
        const channelName = 'PageViews';
        const channelListener = e => this.pushData(e);
        // Событие "page:view" отправляется автоматически.
        // Дополнительные события просмотра страницы могут быть
        // переданы по каналу "PageViews". Такие события могут понадобиться
        // для отслеживания посещаемости определённых страниц, или разделов.
        // Все события объединяются и отправляются с одинаковыми параметрами.
        const pageViewEvents = window.avtoproDataLayer?.getChannel(channelName);

        // Подписываемся на события просмотра страницы, которые могут поступать
        // из кода, выполняющегося ПОСЛЕ старта аналитики.
        // TODO: было бы удобно настроить все слушатели в initDataLayer,
        // но сейчас это не сильно хорошо укладывается, в связи с особенностями
        // реализации DataLayer: работу пришлось бы разделить между двумя методами.
        window.avtoproDataLayer?.subscribe(channelName, channelListener);

        // Если доп. событий просмотра страницы создано несколько,
        // (что технически возможно, хотя логика применения этой возможности
        // до конца не ясна и могла бы даже считаться ошибочной)
        // то отправляем все события индивидуально, чтобы они могли нести
        // свои собственные уникальные параметры (props).
        if (pageViewEvents?.length > 1) {
            return Promise.all([
                this.pushEvent('page:view', undefined, true),
                ...pageViewEvents.map(channelListener)
            ]);
        }
        // В нормальной ситуации, когда кроме "page:view" есть одно
        // доп. событие, они могут быть объединены в один запрос для оптимизации.
        // Параметры событий также объединяются.
        if (pageViewEvents?.length == 1) {
            const { name, ...props } = pageViewEvents[0];
            return this.pushEvent(['page:view', name], props, true);
        }
        return this.pushEvent('page:view', undefined, true);
    }

    /**
     * Подключается к слою обмена данными между компонентами
     * и отправляет поступающие через него события в аналитику.
     */
    private initDataLayer = () => {
        const channelName = 'ProAnalytics';
        const channelListener = e => this.pushData(e);

        if (window.avtoproDataLayer) {
            window.avtoproDataLayer
                .getChannel(channelName)
                ?.forEach(channelListener);

            window.avtoproDataLayer.subscribe(channelName, channelListener);
        } else {
            console.warn('Unable to access avtoproDataLayer.');
        }
    };

    /**
     * Создаёт объект события, готовый к отправке на сервер.
     */
    private createEvent<T extends ProAnalyticsEventCustomProps>(
        name: string | string[],
        props?: T,
        extendedMeta: boolean = false
    ) {
        // Запрещаем свойство props.event, чтобы избежать неоднозначности
        // при одновременной передаче name и props.event, логически имеющих одинаковое назначение
        if (props && Object.prototype.hasOwnProperty.call(props, 'event')) {
            throw new Error(
                'Illegal invocation: property name "event" is reserved for event name.'
            );
        }
        // Итоговое название события
        // Представляет собой строку без пробелов, которая может быть разделена запятыми
        //
        // Приведение переданного имени к массиву необходимо для реализации единого сценария
        // как для одного имени, переданного строкой, так и для массива имен.
        const namesArray = [name].flat().filter(item => item);

        // Дубликаты имён удаляются.
        const uniqueNames = [...new Set(namesArray).values()];

        const event = uniqueNames
            .toString()
            .split(',')
            .map(n => n.replace(/\s+/g, ''))
            .filter(n => n)
            .join(',');

        const { user, urlReferrer } = globals;

        // Параметры рекламных кампаний (UTM-метки),
        // которые могут быть переданы через адресную строку.
        const {
            utm_campaign: utmCampaign,
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_content: utmContent,
            utm_term: utmTerm
        } = parse.query(decodeURI(window.location.search));

        // Понятное обозначение типа пользователя.
        const userType = ['guest', 'buyer', 'seller'][
            Number(!user.isGuest) + Number(user.isSeller)
        ];
        return {
            event,
            pageViewId: this.pageViewId,
            userSession: this.getSession(),
            userAgent: window.navigator.userAgent,
            userId: user.id,
            userType,
            isProSeller: user.isProfessional,
            path: this.href,
            pathType: this.pathType,
            referrer: urlReferrer,
            referrerPageViewId: this.referrerPageViewId,
            referrerPathType: this.referrerPathType,
            cookieConsentGranted:
                getCookie('privacyCookieSettings') ?? 'undefined',
            utmCampaign,
            utmSource,
            utmMedium,
            utmContent,
            utmTerm,
            ...(extendedMeta ? { pageResponse: this.statusCode } : null),
            ...props
        };
    }

    /**
     * Возвращает идентификатор текущей сессии.
     *
     * @todo Этот метод должен быть приватным,
     * но вынужденно сделан публичным для костыля в Product.ts.
     */
    public getSession() {
        return getCookie(this.sessionCookieName);
    }

    /**
     * Устанавливает корневую сессионную куку.
     */
    private setSession(value: string) {
        const dayEnd = ProAnalytics.getDayEnd();
        // Время жизни сессии ограничено текущими сутками,
        // в начале суток всегда начинается новая сессия
        let sessionEnd = new Date(Date.now() + this.sessionDuration);
        if (sessionEnd > dayEnd) {
            sessionEnd = dayEnd;
        }
        setCookie(
            this.sessionCookieName,
            value,
            {
                path: '/',
                expires: sessionEnd
            },
            true /* setRawValue */
        );
    }

    /**
     * Начинает, или продлевает сессию пользователя
     * Promise возвращает идентификатор созданной сессии,
     * идентификатор продлённой сессии не возвращается.
     */
    private startSession() {
        return new Promise<string | undefined>((resolve, reject) => {
            let sessionValue = this.getSession();
            if (!this.hasStarted) {
                reject(new Error('PRO-analytics has not been started yet.'));
            }
            if (sessionValue) {
                // Текущая сессия продлевается
                this.setSession(sessionValue);
                resolve(undefined);
            } else {
                // При отсутствии сессии начинается новая
                sessionValue = `${Date.now()}|${uuidv4()}`;
                this.setSession(sessionValue);
                resolve(sessionValue);
            }
        });
    }

    /**
     * Начинает, или продлевает сессию пользователя
     * и отправляет событие начала сессии на бекенд.
     *
     * @todo Этот метод должен быть приватным,
     * но вынужденно сделан публичным для костыля в Product.ts.
     */
    public pushSession() {
        return this.startSession().then(value => {
            // Начало новой сессии отмечается специальным событием
            if (value) {
                return ProAnalytics.pushAnalyticsData(
                    this.createEvent('page:userSessionStart', undefined, true)
                );
            }
            return undefined;
        });
    }

    /**
     * Настраивает обработчики событий, приводящих к продлению сессии,
     * или пересозданию истёкшей сессии.
     */
    private setEvents() {
        // https://developer.mozilla.org/ru/docs/Web/Events
        const documentEvents = ['submit'];

        // hashchange: The fragment identifier of the URL has changed (the part of the URL after the #).
        // popstate: A session history entry is being navigated to (in certain cases).
        const windowEvents = ['hashchange', 'popstate'];

        documentEvents.forEach(event =>
            document.addEventListener(event, () => {
                this.pushSession();
            })
        );

        windowEvents.forEach(event =>
            window.addEventListener(event, () => {
                this.pushSession();
            })
        );

        setInterval(() => {
            if (window.location.href != this.href) {
                this.href = window.location.href;
                this.pushSession();
            }
        }, 5000);
    }

    /**
     * Отправляет пользовательское событие.
     * @param name - название события (или массив названий)
     * @param props - дополнительные данные по событию
     * @param extendedMeta - передать полные метаданные по событию (page:userSessionStart, page:view)
     */
    public pushEvent<
        T extends ProAnalyticsEventCustomProps = ProAnalyticsEventCustomProps
    >(name: string | string[], props?: T, extendedMeta: boolean = false) {
        // Возникновение пользовательского события автоматически продлевает сессию
        return this.pushSession().then(() =>
            ProAnalytics.pushAnalyticsData(
                this.createEvent<T>(name, props, extendedMeta)
            )
        );
    }

    /**
     * Отправляет пользовательское событие,
     * полученное и виде простого объекта.
     * @param data - объект с данными о событии (наличие поля name обязательно)
     */
    public pushData(
        data: { name: string | string[] } & ProAnalyticsEventCustomProps
    ) {
        if (data && data.name) {
            const { name, ...props } = data;
            return this.pushEvent(name, props);
        }
        return Promise.reject();
    }
}

export default __SERVER__
    ? new ProAnalytics({ statusCode: 0, isBot: false, href: '' })
    : new ProAnalytics({
          statusCode: Number(
              document
                  .querySelector<HTMLMetaElement>(
                      'meta[name="response-status-code"]'
                  )
                  ?.getAttribute('content') ?? 0
          ),
          isBot:
              document.querySelector<HTMLMetaElement>(
                  'meta[name="feed-context"]'
              )?.dataset.isSearchSystem === 'true',
          href: window.location.href
      }).start();
